import React from 'react'
import { Grid, Autocomplete, Checkbox, FormControlLabel, Divider } from "@mui/material";
import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
//Components
import CMLabel from '../../components/atoms/CMLabel';
import CMButton from '../../components/atoms/CMButton';
import CMTextField from '../../components/atoms/CMTextField';
//Context
import { UserContext } from '../../context/UserContext';
//Services
import * as UsuarioService from '../../services/UsuarioService';

const PermisosUsuarios = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [usuarioSel, setUsuarioSel] = useState(null);
    const [usuarioOrigen, setUsuarioOrigen] = useState(null);
    const [permisos, setPermisos] = useState([]);
    const [permisosOriginales, setPermisosOriginales] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user } = useContext(UserContext);
    const location = useLocation();

    useEffect(() => {
        UsuarioService.getUsuariosTodos(setUsuarios, user.token);
    }, []);

    useEffect(() => {
        const idPreseleccionado = location.state?.id_usuario;
        if (idPreseleccionado && usuarios.length > 0) {
            const encontrado = usuarios.find(u => u.id_usuario === idPreseleccionado);
            if (encontrado) setUsuarioSel(encontrado);
        }
    }, [usuarios]);

    const cargarPermisos = (id_usuario) => {
        UsuarioService.getPermisosUsuario(id_usuario, (data) => {
            setPermisos(data);
            setPermisosOriginales(data);
        }, user.token);
    };

    useEffect(() => {
        if (usuarioSel) {
            cargarPermisos(usuarioSel.id_usuario);
        } else {
            setPermisos([]);
            setPermisosOriginales([]);
        }
    }, [usuarioSel]);

    const handleToggle = (id_submodulo) => (event) => {
        setPermisos(permisos.map(p =>
            p.id_submodulo === id_submodulo ? { ...p, asignado: event.target.checked } : p
        ));
    };

    const handleToggleModulo = (id_modulo, asignar) => () => {
        setPermisos(permisos.map(p =>
            p.id_modulo === id_modulo ? { ...p, asignado: asignar } : p
        ));
    };

    const handleCopiar = () => {
        if (!usuarioOrigen || !usuarioSel) return;
        Swal.fire({
            title: 'Replicar Permisos',
            text: `Se reemplazarán los permisos de ${usuarioSel.usuario} con los de ${usuarioOrigen.usuario}. ¿Continuar?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, replicar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const response = await UsuarioService.copiarPermisos(usuarioOrigen.id_usuario, usuarioSel.id_usuario, user.token);
                    Swal.fire('Correcto', response.mensaje, 'success');
                    setUsuarioOrigen(null);
                    cargarPermisos(usuarioSel.id_usuario);
                } catch (error) {
                    const mensaje = error.response?.data?.mensaje || 'Ocurrió un error al replicar los permisos';
                    Swal.fire('Error', mensaje, 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const guardar = async () => {
        setLoading(true);
        try {
            const ids = permisos.filter(p => p.asignado).map(p => p.id_submodulo);
            const result = await UsuarioService.guardarPermisos(usuarioSel.id_usuario, ids, user.token);
            Swal.fire('Correcto', result.mensaje, 'success');
            cargarPermisos(usuarioSel.id_usuario);
        } catch (error) {
            const mensaje = error.response?.data?.mensaje || 'Ocurrió un error al guardar los permisos';
            Swal.fire('Error', mensaje, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleGuardar = () => {
        if (!usuarioSel) return;
        const desactivados = permisosOriginales.filter(o =>
            o.asignado && !permisos.find(p => p.id_submodulo === o.id_submodulo)?.asignado
        );
        if (desactivados.length > 0) {
            const nombres = desactivados.map(d => `• ${d.nombre_modulo} / ${d.nombre_submodulo}`).join('<br/>');
            Swal.fire({
                title: 'Desactivar permisos',
                html: `Se desactivarán ${desactivados.length} permiso(s) de <b>${usuarioSel.usuario}</b>:<br/><br/>${nombres}<br/><br/>Podrá reactivarlos marcando el check nuevamente. ¿Continuar?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, guardar',
                cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) guardar();
            });
        } else {
            guardar();
        }
    };

    const modulos = permisos.reduce((grupos, p) => {
        if (!grupos[p.id_modulo]) {
            grupos[p.id_modulo] = { nombre_modulo: p.nombre_modulo, submodulos: [] };
        }
        grupos[p.id_modulo].submodulos.push(p);
        return grupos;
    }, {});

    return (
        <Grid width={'100%'} m="auto">
            <Grid container justifyContent="flex-start" alignItems="center" spacing={2} sx={{ py: 1 }}>
                <Grid container item xs={12} sm={12}>
                    <Grid item sm={6}>
                        <CMLabel sx={{ margin: 2 }} fontSize='24px' color="black"><b>Permisos de Usuarios</b></CMLabel>
                    </Grid>
                </Grid>
                <Grid container item xs={12} sm={12} justifyContent="center">
                    <Grid item sm={12}>
                        <hr></hr>
                        <Grid container item xs={12} sm={12} alignItems="center" spacing={2} sx={{ py: 1 }}>
                            <Grid item xs={12} sm={4}>
                                <Autocomplete
                                    options={usuarios}
                                    getOptionLabel={(option) => `${option.usuario} - ${option.nom_completo || ''} (${option.nombre_rol})`}
                                    value={usuarioSel}
                                    onChange={(e, newValue) => setUsuarioSel(newValue)}
                                    isOptionEqualToValue={(option, value) => option.id_usuario === value.id_usuario}
                                    renderInput={(params) => (
                                        <CMTextField {...params} label="Usuario" variant="filled" />
                                    )}
                                />
                            </Grid>
                            {usuarioSel && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <Autocomplete
                                            options={usuarios.filter(u => u.id_usuario !== usuarioSel.id_usuario)}
                                            getOptionLabel={(option) => `${option.usuario} - ${option.nom_completo || ''} (${option.nombre_rol})`}
                                            value={usuarioOrigen}
                                            onChange={(e, newValue) => setUsuarioOrigen(newValue)}
                                            isOptionEqualToValue={(option, value) => option.id_usuario === value.id_usuario}
                                            renderInput={(params) => (
                                                <CMTextField {...params} label="Copiar permisos de..." variant="filled" />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <CMButton
                                            label="Replicar"
                                            variant="outlined"
                                            disabled={loading || !usuarioOrigen}
                                            onClick={handleCopiar}
                                        />
                                        <CMButton
                                            label="Guardar"
                                            variant="contained"
                                            sx={{ marginLeft: '10px' }}
                                            disabled={loading}
                                            onClick={handleGuardar}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                {usuarioSel && (
                    <Grid container item xs={12} sm={12} spacing={2} sx={{ py: 1 }}>
                        {Object.keys(modulos).map(idModulo => (
                            <Grid item xs={12} sm={4} key={idModulo}>
                                <CMLabel fontSize='16px' color="black"><b>{modulos[idModulo].nombre_modulo}</b></CMLabel>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={modulos[idModulo].submodulos.every(s => s.asignado)}
                                                    indeterminate={modulos[idModulo].submodulos.some(s => s.asignado) && !modulos[idModulo].submodulos.every(s => s.asignado)}
                                                    onChange={(e) => handleToggleModulo(Number(idModulo), e.target.checked)()}
                                                />
                                            }
                                            label={<CMLabel fontSize='12px' color="gray"><i>Todos</i></CMLabel>}
                                        />
                                    </Grid>
                                    {modulos[idModulo].submodulos.map(sub => (
                                        <Grid item xs={12} key={sub.id_submodulo} sx={{ pl: 2 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={sub.asignado}
                                                        onChange={handleToggle(sub.id_submodulo)}
                                                    />
                                                }
                                                label={sub.nombre_submodulo}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Divider sx={{ my: 1 }} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Grid>
        </Grid>
    )
}

export default PermisosUsuarios
